import type { JSX } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Trans, useTranslation } from 'react-i18next';

import type { PlaceType } from '@/api/types/Place';

// Prototype `#s-empty` — the `.empty` block inside the list card: faint pin
// glyph, a bold headline naming WHY the list is empty, and one hint line. When
// a filter narrowed the list to nothing the hint carries a „zrušte filtr" link
// (the only way out that does not require typing).
//
// Rendered by BOTH trees: the desktop table drops it into a full-width cell,
// the card tree renders it in place of the cards.

const ICON_SIZE_PX = 28;
const TAP_TARGET_MIN_PX = 44;

export interface PlacesEmptyStateProps {
  // The active type chip, or `null` for „Vše".
  typeFilter: PlaceType | null;
  // Whether the search box holds a needle.
  hasQuery: boolean;
  // Viewers get no „založte první" advice — they cannot create.
  canWrite: boolean;
  onClearFilter: () => void;
}

export const PlacesEmptyState = ({
  typeFilter,
  hasQuery,
  canWrite,
  onClearFilter,
}: PlacesEmptyStateProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('places');

  const hasFilter = typeFilter !== null || hasQuery;
  const title = hasQuery
    ? t('empty.titleSearch')
    : typeFilter !== null
      ? t('empty.titleType', { type: t(`type.${typeFilter}`) })
      : t('empty.title');
  const hintKey = canWrite
    ? hasFilter
      ? 'empty.hintCreateFilter'
      : 'empty.hintCreate'
    : hasFilter
      ? 'empty.hintFilter'
      : 'empty.hintNone';

  return (
    <Box
      data-testid="places-empty"
      sx={{
        px: theme.spacing(2.5),
        py: theme.spacing(5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: 13,
      }}
    >
      <PlaceOutlinedIcon
        aria-hidden
        sx={{ fontSize: ICON_SIZE_PX, color: theme.palette.text.disabled, mb: 1 }}
      />
      <Typography
        component="b"
        sx={{
          display: 'block',
          color: theme.palette.text.primary,
          fontSize: 14,
          fontWeight: 600,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {/* The hint line itself carries the 44px touch height (CQE W2): an
          absolutely-positioned overlay on the inline button used to bleed
          upwards into the headline's hit area. `pre-wrap` keeps the spaces
          around the inline <Trans> slot that flex-item blockification would
          otherwise collapse. */}
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          minHeight: TAP_TARGET_MIN_PX,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'pre-wrap',
        }}
      >
        <Trans
          i18nKey={`places:${hintKey}`}
          components={[
            <Box
              key="clear"
              component="button"
              type="button"
              data-testid="places-clear-filter"
              onClick={onClearFilter}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                font: 'inherit',
                // The hit area IS the button — 44px tall inside the hint's
                // 44px line box, so it can never reach the headline above.
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: TAP_TARGET_MIN_PX,
                verticalAlign: 'middle',
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            />,
          ]}
        />
      </Typography>
    </Box>
  );
};
